import React from 'react';
import { cn } from '@/lib/utils';
import {
  formatDisplayDateTime,
  formatDisplayDate,
  formatShortDateTime,
  getRelativeTime,
  isDateInPast,
  isDateInFuture,
} from '@/lib/utils';

interface DateTimeDisplayProps {
  date: Date | string | null | undefined;
  format?: 'full' | 'short' | 'date-only' | 'relative';
  className?: string;
  showTooltip?: boolean;
  locale?: string;
}

/**
 * Timezone-aware datetime display component
 * Automatically converts UTC datetime to user's local timezone for display
 */
export const DateTimeDisplay: React.FC<DateTimeDisplayProps> = ({
  date,
  format = 'full',
  className,
  showTooltip = true,
  locale = 'de-DE',
}) => {
  if (!date) {
    return <span className={cn('text-muted-foreground', className)}>—</span>;
  }

  const getFormattedDate = () => {
    switch (format) {
      case 'short':
        return formatShortDateTime(date);
      case 'date-only':
        return formatDisplayDate(date);
      case 'relative':
        return getRelativeTime(date);
      case 'full':
      default:
        return formatDisplayDateTime(date, { locale });
    }
  };

  const getTooltipContent = () => {
    if (!showTooltip || format === 'full') return undefined;

    // Show full datetime in tooltip for other formats
    return formatDisplayDateTime(date, { locale });
  };

  const getStatusClasses = () => {
    if (format === 'relative') {
      if (isDateInPast(date)) {
        return 'text-muted-foreground';
      }
      if (isDateInFuture(date)) {
        return 'text-primary';
      }
    }
    return '';
  };

  const formattedDate = getFormattedDate();
  const tooltipContent = getTooltipContent();
  const statusClasses = getStatusClasses();

  return (
    <span className={cn(statusClasses, className)} title={tooltipContent}>
      {formattedDate}
    </span>
  );
};

interface DateRangeDisplayProps {
  startDate: Date | string | null | undefined;
  endDate: Date | string | null | undefined;
  format?: 'full' | 'short' | 'date-only';
  className?: string;
  locale?: string;
  separator?: string;
}

/**
 * Display a date range with proper timezone handling
 */
export const DateRangeDisplay: React.FC<DateRangeDisplayProps> = ({
  startDate,
  endDate,
  format = 'short',
  className,
  locale = 'de-DE',
  separator = ' – ',
}) => {
  if (!startDate || !endDate) {
    return <span className={cn('text-muted-foreground', className)}>—</span>;
  }

  return (
    <span className={className}>
      <DateTimeDisplay date={startDate} format={format} locale={locale} showTooltip={false} />
      {separator}
      <DateTimeDisplay date={endDate} format={format} locale={locale} showTooltip={false} />
    </span>
  );
};

interface ValidityStatusProps {
  startDate: Date | string;
  endDate: Date | string;
  className?: string;
}

/**
 * Show validity status with visual indicators
 */
export const ValidityStatus: React.FC<ValidityStatusProps> = ({ startDate, endDate, className }) => {
  const now = new Date();
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const isActive = now >= start && now <= end;
  const isPending = now < start;
  const isExpired = now > end;

  const getStatusInfo = () => {
    if (isActive) {
      return {
        label: 'Aktiv',
        color: 'text-green-600 bg-green-100',
        relativeEnd: getRelativeTime(end),
      };
    }
    if (isPending) {
      return {
        label: 'Startet',
        color: 'text-blue-600 bg-blue-100',
        relativeEnd: getRelativeTime(start),
      };
    }
    if (isExpired) {
      return {
        label: 'Abgelaufen',
        color: 'text-red-600 bg-red-100',
        relativeEnd: getRelativeTime(end),
      };
    }
    return {
      label: 'Unbekannt',
      color: 'text-gray-600 bg-gray-100',
      relativeEnd: '',
    };
  };

  const { label, color, relativeEnd } = getStatusInfo();

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className={cn('px-2 py-1 rounded-full text-xs font-medium', color)}>{label}</span>
      {relativeEnd && <span className='text-xs text-muted-foreground'>bis {relativeEnd}</span>}
    </div>
  );
};
